import classNames from "classnames";
import Checkbox from "components/Checkbox";
import { QuartileBar } from "components/QuartileBar";
import { studyTypeBadges } from "helpers/studyTypes";
import useLabels from "hooks/useLabels";
import { ChangeEvent, useEffect, useState } from "react";
import {
  FeedbackInputType,
  FeedbackItemContent,
  UNKNOWN_QUARTILE_VALUE,
} from "./FeedbackUtil";

type PropsType = {
  payload: FeedbackItemContent;
  onChange: (id: string, values: FeedbackInputType) => void;
};

const STUDY_TYPE_KEY = "paperDetails:studyType";
const SJR_SCORE_KEY = "journalDetails:sjrScore";
const quartiles = [1, 2, 3, 4, UNKNOWN_QUARTILE_VALUE];

const FeedbackItem = (props: PropsType) => {
  const { onChange, payload } = props;
  const [checked, setChecked] = useState<boolean>(false);
  const [selectedStudyType, setSelectedStudyType] = useState("");
  const [selectedSjrScore, setSelectedSjrScore] = useState("");
  const [fields, setFields] = useState<FeedbackInputType>({
    description: "",
    corrected: "",
    pristine: true,
    original: "",
  });

  const [generalLabels, feedbackLabels] = useLabels(
    "general",
    "human-feedback-modal"
  );

  const toggleCheckbox = () => {
    setChecked((prev) => !prev);

    if (payload.name === STUDY_TYPE_KEY) {
      if (payload.original) {
        const originalStudyType = studyTypeBadges.find(
          (item) =>
            item.label.toLowerCase() === `${payload.original}`.toLowerCase()
        );
        setSelectedStudyType(originalStudyType?.value as string);
      } else {
        setSelectedStudyType(payload.original as string);
      }
    }
    if (payload.name === SJR_SCORE_KEY) {
      setSelectedSjrScore(payload.original as string);
    }
    setFields((prev) => ({ ...prev, pristine: false }));
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFields((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const handleToggleStudyTypes = (value: string) => () => {
    if (value === selectedStudyType) {
      setSelectedStudyType("");
      setFields((prev) => ({ ...prev, corrected: "" }));
    } else {
      setSelectedStudyType(value);
      setFields((prev) => ({ ...prev, corrected: value }));
    }
  };

  const handleSjrScoreToggle = (value: string | number) => () => {
    const score = `${value}`;
    if (score === selectedSjrScore) {
      setSelectedSjrScore("");
      setFields((prev) => ({ ...prev, corrected: "" }));
    } else {
      setSelectedSjrScore(score);
      setFields((prev) => ({ ...prev, corrected: score }));
    }
  };

  useEffect(() => {
    onChange(payload.name, { ...fields, original: `${payload.original}` });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, payload.name, onChange]);

  const renderStudyTypes = () => {
    return (
      <div className="flex flex-wrap gap-2">
        {studyTypeBadges.map((option, optionIdx) => {
          return (
            <div
              key={optionIdx}
              className="border border-green-emphasis bg-bg-base p-2 rounded-lg flex items-center gap-2 tiny-md"
            >
              <Checkbox
                isChecked={selectedStudyType === option.value}
                onToggle={handleToggleStudyTypes(option.value)}
                label={option.label}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderSjrScore = () => {
    return (
      <div className="flex overflow-x-auto gap-2">
        {quartiles.map((option) => {
          return (
            <button
              key={option}
              onClick={handleSjrScoreToggle(option)}
              className={classNames(
                "flex items-center gap-3 px-3 py-2 border border-border-base rounded-lg sm-md",
                {
                  "bg-accent-bg-subtle": `${selectedSjrScore}` === `${option}`,
                }
              )}
            >
              {option === UNKNOWN_QUARTILE_VALUE ? (
                <>
                  <span>{option}</span>
                  <QuartileBar quartile={null} />
                </>
              ) : (
                <>
                  <span>Q{option}</span>
                  <QuartileBar quartile={parseInt(option as string)} />
                </>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  const isFreeText =
    typeof payload.content === "string" || typeof payload.content === "number";

  const renderContent = () => {
    return isFreeText && payload.content === "n/a"
      ? "Nothing extracted"
      : payload.content;
  };

  return (
    <div>
      <div className="flex gap-2 sm-md mb-2">
        <Checkbox
          isChecked={checked}
          onToggle={toggleCheckbox}
          data-testid="checkbox"
          label={payload.label}
        />
      </div>
      <div
        className={classNames(
          "border-l-[3px] border-l-border-base pl-3 flex flex-col gap-2"
        )}
      >
        <div
          className={classNames({
            "text-fg-muted": isFreeText,
          })}
        >
          {payload.name === STUDY_TYPE_KEY && checked
            ? renderStudyTypes()
            : payload.name === SJR_SCORE_KEY && checked
              ? renderSjrScore()
              : renderContent()}
        </div>
        {checked ? (
          <div>
            <form className="flex flex-col gap-3">
              {((payload.name === STUDY_TYPE_KEY && !!selectedStudyType) ||
                payload.name !== STUDY_TYPE_KEY) && (
                <label className="relative">
                  <span className="sm-md">
                    {feedbackLabels["feedback-item-explain-label"]}
                  </span>
                  <input
                    name="description"
                    data-testid="description"
                    value={fields.description}
                    className="block border border-border-base rounded-lg px-4 h-[50px] w-full"
                    onChange={handleChange}
                  />
                  <span className="absolute right-0 top-0 tiny-normal text-fg-muted">
                    {generalLabels["optional-text"]}
                  </span>
                </label>
              )}
              {isFreeText && (
                <label className="relative">
                  <span className="sm-md">
                    {feedbackLabels["feedback-item-revise-label"]}
                  </span>
                  <input
                    name="corrected"
                    data-testid="corrected"
                    value={fields.corrected}
                    className="block border border-border-base rounded-lg px-4 h-[50px] w-full"
                    onChange={handleChange}
                  />
                  <span className="absolute right-0 top-0 tiny-normal text-fg-muted">
                    {generalLabels["optional-text"]}
                  </span>
                </label>
              )}
            </form>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export { FeedbackItem };
